import { useCallback, useEffect, useState } from "react";
import { Pencil, Users, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import ProjectMembersDialog from "../components/ProjectMembersDialog";

const PM_ROLES = ["PM", "ADMIN", "LEAD"];
const NEW_CLIENT = "__new__";
const STATUSES = ["ACTIVE", "ON_HOLD", "CLOSED"];

const emptyForm = () => ({
  key: "", name: "", client_id: "", client_name: "", description: "", project_manager_id: "",
  scrum_master_id: "", report_open_time: "06:00", report_due_time: "10:00", next_milestone_date: "", status: "ACTIVE", methodology: "SCRUM",
});

function ProjectDialog({ open, onOpenChange, initial, users, clients, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial;
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...emptyForm(), ...initial, client_id: initial.client_id || "", client_name: "",
      description: initial.description || "", next_milestone_date: initial.next_milestone_date || "",
      scrum_master_id: initial.scrum_master_id || "", methodology: initial.methodology || "SCRUM",
    } : emptyForm());
  }, [open, initial]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const managers = users.filter((u) => PM_ROLES.includes(u.role) && u.status === "ACTIVE");

  const save = async () => {
    setBusy(true);
    const payload = {
      name: form.name, description: form.description || null,
      client_id: form.client_id && form.client_id !== NEW_CLIENT ? form.client_id : null,
      client_name: form.client_id === NEW_CLIENT ? form.client_name : null,
      project_manager_id: form.project_manager_id, scrum_master_id: form.scrum_master_id || null,
      report_open_time: form.report_open_time, report_due_time: form.report_due_time,
      next_milestone_date: form.next_milestone_date || (isEdit ? "" : null),
      methodology: form.methodology,
    };
    try {
      if (isEdit) {
        await api.patch(`/admin/projects/${initial.id}`, { ...payload, status: form.status });
        toast.success(t("projectUpdated"));
      } else {
        await api.post("/admin/projects", { ...payload, key: form.key });
        toast.success(t("projectCreated"));
      }
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="project-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? t("editProject") : t("addProject")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>{t("projectKey")}</Label>
              <Input data-testid="project-key-input" placeholder="EMS" value={form.key} disabled={isEdit}
                onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase() })} /></div>
            <div className="space-y-1 col-span-2"><Label>{t("projectName")}</Label>
              <Input data-testid="project-name-input" value={form.name} onChange={set("name")} /></div>
          </div>
          <div className="space-y-1"><Label>{t("client")}</Label>
            <Select value={form.client_id || undefined} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="project-client-select"><SelectValue placeholder={t("selectClient")} /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                <SelectItem value={NEW_CLIENT} data-testid="project-client-new-option">{t("newClient")}</SelectItem>
              </SelectContent>
            </Select>
            {form.client_id === NEW_CLIENT && (
              <Input data-testid="project-client-name-input" className="mt-2" placeholder={t("clientName")}
                value={form.client_name} onChange={set("client_name")} />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("methodology")}</Label>
              <Select value={form.methodology} onValueChange={(v) => setForm({ ...form, methodology: v })}>
                <SelectTrigger data-testid="project-methodology-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SCRUM">Scrum</SelectItem><SelectItem value="KANBAN">Kanban</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1"><Label>{t("description")}</Label>
            <Textarea data-testid="project-description-input" rows={2} value={form.description} onChange={set("description")} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("projectManager")}</Label>
              <Select value={form.project_manager_id || undefined} onValueChange={(v) => setForm({ ...form, project_manager_id: v })}>
                <SelectTrigger data-testid="project-pm-select"><SelectValue placeholder={t("selectPm")} /></SelectTrigger>
                <SelectContent>
                  {managers.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`project-pm-option-${u.email.split("@")[0]}`}>{u.display_name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>{t("scrumMaster")}</Label>
              <Select value={form.scrum_master_id || undefined} onValueChange={(v) => setForm({ ...form, scrum_master_id: v })}>
                <SelectTrigger data-testid="project-sm-select"><SelectValue placeholder="= PM" /></SelectTrigger>
                <SelectContent>
                  {managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.display_name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1"><Label>{t("reportWindow")}</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground w-12">{t("opensAt")}</span>
                <Input data-testid="project-open-time-input" type="time" value={form.report_open_time} onChange={set("report_open_time")} /></div>
              <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground w-12">{t("dueAt")}</span>
                <Input data-testid="project-due-time-input" type="time" value={form.report_due_time} onChange={set("report_due_time")} /></div>
            </div></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("nextMilestone")}</Label>
              <Input data-testid="project-milestone-input" type="date" value={form.next_milestone_date} onChange={set("next_milestone_date")} /></div>
            {isEdit && (
              <div className="space-y-1"><Label>{t("projectStatus")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
          </div>
          <Button data-testid="project-save-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]"
            disabled={busy || !form.name || !form.project_manager_id || (!isEdit && !form.key)} onClick={save}>
            {busy ? "..." : t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProjects() {
  const { t } = useLang();
  const [projects, setProjects] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [members, setMembers] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get("/admin/projects"), api.get("/admin/users"), api.get("/admin/clients")])
      .then(([p, u, c]) => { setProjects(p.data); setUsers(u.data); setClients(c.data); })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!projects) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-4" data-testid="admin-projects-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{projects.length === 0 ? t("noProjectsAdmin") : `${projects.length} ${t("projects").toLowerCase()}`}</p>
        <Button data-testid="admin-add-project-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]" onClick={() => setDialog({ open: true, initial: null })}>
          <Plus className="h-4 w-4 mr-1.5" />{t("addProject")}
        </Button>
      </div>
      <div className="apha-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("projectKey")}</TableHead><TableHead>{t("projectName")}</TableHead><TableHead>{t("client")}</TableHead><TableHead>PM</TableHead>
            <TableHead>{t("members")}</TableHead><TableHead>{t("projectStatus")}</TableHead><TableHead>{t("reportWindow")}</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {projects.map((p) => (
              <TableRow key={p.id} data-testid={`admin-project-row-${p.key}`}>
                <TableCell className="font-medium">{p.key}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell className="text-muted-foreground">{p.client_name}</TableCell>
                <TableCell>{p.pm_name}</TableCell>
                <TableCell data-testid={`admin-project-members-count-${p.key}`}>{p.member_count}</TableCell>
                <TableCell><span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${p.status === "ACTIVE" ? "text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]" : "text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]"}`}>{p.status}</span></TableCell>
                <TableCell className="text-muted-foreground">{p.report_open_time}–{p.report_due_time}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`admin-project-members-${p.key}`} onClick={() => setMembers(p)}>
                    <Users className="h-3.5 w-3.5 mr-1" />{t("members")}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`admin-project-edit-${p.key}`} onClick={() => setDialog({ open: true, initial: p })}>
                    <Pencil className="h-3.5 w-3.5 mr-1" />{t("editProject")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {projects.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8" data-testid="admin-projects-empty">{t("noData")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <ProjectDialog open={dialog.open} initial={dialog.initial} users={users} clients={clients}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={load} />
      <ProjectMembersDialog project={members} open={!!members} onOpenChange={(o) => !o && setMembers(null)} onChanged={load} />
    </div>
  );
}
